import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { HeaderTip } from "@/components/HeaderTip";
import { Bell, Warning, Package, CalendarBlank, Receipt, ChatCircleDots, HandCoins, Repeat, TrendUp } from "@phosphor-icons/react";

const ICONS = { low_stock: Package, out_of_stock: Warning, empty_day: CalendarBlank, open_slot: CalendarBlank, pending_receipts: Receipt, stale_scan: Receipt, no_scans: Receipt, rowan_notice: ChatCircleDots, vendor_insight: HandCoins, anomaly_alert: Warning, subscription_cancel_reminder: Repeat, subscription_price_hike: TrendUp };

export function AlertBell() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef(null);

  const refresh = () => api.get("/alerts").then((r) => setAlerts(r.data?.alerts || [])).catch(() => {});
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const count = alerts.length;

  return (
    <div className="relative" ref={ref}>
      <HeaderTip label="Notifications">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="alert-bell"
          className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors"
          style={{ color: "#FFFFFF" }}
          aria-label={count > 0 ? `Notifications, ${count} new` : "Notifications"}
        >
          <Bell size={18} weight="duotone" />
          {count > 0 && (
            <>
              <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full animate-ping" style={{ background: "var(--bk-gold)" }} />
              <span data-testid="alert-bell-count" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "var(--bk-gold)", color: "var(--bk-accent)" }}>{count}</span>
            </>
          )}
        </button>
      </HeaderTip>

      {open && (
        <div
          data-testid="alert-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-xl border shadow-xl z-50 bk-scroll"
          style={{ background: "var(--bk-surface)", borderColor: "var(--bk-border)" }}
        >
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--bk-border)" }}>
            <Bell size={16} weight="fill" style={{ color: "var(--bk-energy)" }} />
            <span className="font-display font-bold text-sm" style={{ color: "var(--bk-text)" }}>Active Assistant</span>
          </div>
          {count === 0 ? (
            <div className="px-4 py-6 text-center text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="alert-empty">
              All clear — nothing needs you right now.
            </div>
          ) : (
            alerts.map((a, i) => {
              const Icon = ICONS[a.type] || Warning;
              return (
                <button
                  key={i}
                  onClick={() => { setOpen(false); navigate(`/business/${a.business_id}${a.tab ? `?tab=${a.tab}` : ""}`); }}
                  data-testid={`alert-item-${i}`}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#F9F7F1] border-b transition-colors"
                  style={{ borderColor: "var(--bk-border)" }}
                >
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: a.severity === "high" ? "var(--bk-energy-bg)" : "var(--bk-accent-glow)" }}>
                    <Icon size={15} weight="fill" style={{ color: a.severity === "high" ? "var(--bk-energy)" : "var(--bk-accent)" }} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{a.title}</span>
                    <span className="block text-xs mt-0.5" style={{ color: "var(--bk-text-2)" }}>{a.detail}</span>
                    {a.business_name && <span className="block text-[11px] mt-1 font-medium" style={{ color: "var(--bk-accent)" }}>{a.business_name}</span>}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
